/**
 * Dependency Graph Visualization Component
 *
 * Displays service dependencies using React Flow for interactive graph visualization
 */

import { useMemo, useCallback, useEffect } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  BackgroundVariant,
  MiniMap,
  Panel,
  useNodesState,
  useEdgesState,
  MarkerType,
  type OnNodesChange,
  type OnEdgesChange,
} from 'reactflow';
import 'reactflow/dist/style.css';
import type { Service } from '../../graphql/types';
import { ServiceType, ServiceTier } from '../../graphql/types';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { EmptyState } from '../shared/EmptyState';
import './DependencyGraph.css';

/**
 * Dependency with nested service data from GraphQL
 */
interface GraphQLDependency {
  fromServiceId: string;
  toServiceId: string;
  metadata: {
    dependencyType: string;
    communicationPattern: string;
    criticality?: string | null;
    notes?: string | null;
  };
  toService?: Service | null;
  fromService?: Service | null;
}

/**
 * Props for DependencyGraph component
 */
interface DependencyGraphProps {
  /** The focal service at the center of the graph */
  service: Service;
  /** Dependencies (services this service depends on) */
  dependencies: GraphQLDependency[];
  /** Dependents (services that depend on this service) */
  dependents: GraphQLDependency[];
  /** Whether data is loading */
  loading?: boolean;
  /** Error message if any */
  error?: string | null;
}

/**
 * Get color for service node based on service type.
 *
 * Sourced from Forge theme tokens (not raw hex) so node fills adapt when the
 * app switches to dark mode; the hex is only a fallback for the rare case a
 * token fails to resolve (issue #653).
 */
function getServiceTypeColor(serviceType: ServiceType): string {
  switch (serviceType) {
    case ServiceType.Application:
      return 'var(--forge-theme-primary, #2196F3)';
    case ServiceType.Integration:
      return 'var(--forge-theme-success, #4CAF50)';
    case ServiceType.Platform:
      return 'var(--forge-theme-warning, #FF9800)';
    case ServiceType.Infrastructure:
      return 'var(--forge-theme-tertiary, #9C27B0)';
    case ServiceType.Website:
      return 'var(--forge-theme-secondary, #009688)';
    /* c8 ignore next 2 — serviceType is typed as the ServiceType enum, whose
       members are all handled above; this is a defensive fallback for
       malformed runtime data, not a reachable branch under normal typing. */
    default:
      return 'var(--forge-theme-outline-high, #757575)';
  }
}

/**
 * Get the themed stroke/marker color for an edge based on dependency type
 * (hard vs. soft). Sourced from Forge theme tokens (issue #653).
 */
function getEdgeColor(dependencyType: string): string {
  return dependencyType === 'hard'
    ? 'var(--forge-theme-primary, #2196F3)'
    : 'var(--forge-theme-outline-high, #9E9E9E)';
}

/**
 * Get border color based on tier (criticality).
 *
 * Sourced from Forge theme tokens (issue #653) — see getServiceTypeColor.
 */
function getTierBorderColor(tier: ServiceTier): string {
  switch (tier) {
    case ServiceTier.Tier0:
      return 'var(--forge-theme-error, #D32F2F)'; // Critical
    case ServiceTier.Tier1:
      return 'var(--forge-theme-warning, #F57C00)'; // High
    case ServiceTier.Tier2:
      return 'var(--forge-theme-info, #FBC02D)'; // Medium
    case ServiceTier.Tier3:
      return 'var(--forge-theme-success, #689F38)'; // Low
    /* c8 ignore next 2 — tier is typed as the ServiceTier enum, whose members
       are all handled above; defensive fallback for malformed runtime data. */
    default:
      return 'var(--forge-theme-outline-high, #757575)';
  }
}

/**
 * DependencyGraph Component
 */
export default function DependencyGraph({
  service,
  dependencies,
  dependents,
  loading = false,
  error = null,
}: DependencyGraphProps): JSX.Element {
  // Build nodes and edges for React Flow
  const { nodes: initialNodes, edges: initialEdges } = useMemo(() => {
    const nodes: Node[] = [];
    const edges: Edge[] = [];

    // Center node (focal service)
    nodes.push({
      id: service.entityId,
      type: 'default',
      position: { x: 400, y: 300 },
      data: {
        label: (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{service.name}</div>
            <div
              style={{ fontSize: '11px', color: 'var(--forge-theme-text-medium-inverse, #666)' }}
            >
              {service.serviceType}
            </div>
            <div style={{ fontSize: '10px', color: 'var(--forge-theme-text-low-inverse, #999)' }}>
              {service.tier}
            </div>
          </div>
        ),
      },
      style: {
        background: getServiceTypeColor(service.serviceType),
        color: 'var(--forge-theme-text-high-inverse, #fff)',
        border: `3px solid ${getTierBorderColor(service.tier)}`,
        borderRadius: '8px',
        padding: '12px',
        width: 180,
        fontSize: '12px',
      },
    });

    // Dependency nodes (services this service depends on) - positioned to the left
    dependencies.forEach((dep, index) => {
      if (dep.toService) {
        const depService = dep.toService;
        const yOffset = (index - dependencies.length / 2) * 120;

        nodes.push({
          id: depService.entityId,
          type: 'default',
          position: { x: 50, y: 300 + yOffset },
          data: {
            label: (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontWeight: 'bold', fontSize: '12px' }}>{depService.name}</div>
                <div
                  style={{
                    fontSize: '10px',
                    color: 'var(--forge-theme-text-medium-inverse, #666)',
                  }}
                >
                  {depService.serviceType}
                </div>
              </div>
            ),
          },
          style: {
            background: getServiceTypeColor(depService.serviceType),
            color: 'var(--forge-theme-text-high-inverse, #fff)',
            border: `2px solid ${getTierBorderColor(depService.tier)}`,
            borderRadius: '6px',
            padding: '10px',
            width: 150,
            fontSize: '11px',
          },
        });

        // Edge from dependency to focal service
        edges.push({
          id: `${depService.entityId}-${service.entityId}`,
          source: depService.entityId,
          target: service.entityId,
          label: dep.metadata.communicationPattern,
          type: 'smoothstep',
          animated: dep.metadata.dependencyType === 'hard',
          style: {
            stroke: getEdgeColor(dep.metadata.dependencyType),
            strokeWidth: dep.metadata.dependencyType === 'hard' ? 2 : 1,
            strokeDasharray: dep.metadata.dependencyType === 'soft' ? '5,5' : undefined,
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: getEdgeColor(dep.metadata.dependencyType),
          },
          labelStyle: {
            fontSize: '10px',
            fill: 'var(--forge-theme-text-medium, #666)',
          },
          labelBgStyle: {
            fill: 'var(--forge-theme-surface, #fff)',
          },
        });
      }
    });

    // Dependent nodes (services that depend on this service) - positioned to the right
    dependents.forEach((dep, index) => {
      if (dep.fromService) {
        const depService = dep.fromService;
        const yOffset = (index - dependents.length / 2) * 120;

        nodes.push({
          id: depService.entityId,
          type: 'default',
          position: { x: 750, y: 300 + yOffset },
          data: {
            label: (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontWeight: 'bold', fontSize: '12px' }}>{depService.name}</div>
                <div
                  style={{
                    fontSize: '10px',
                    color: 'var(--forge-theme-text-medium-inverse, #666)',
                  }}
                >
                  {depService.serviceType}
                </div>
              </div>
            ),
          },
          style: {
            background: getServiceTypeColor(depService.serviceType),
            color: 'var(--forge-theme-text-high-inverse, #fff)',
            border: `2px solid ${getTierBorderColor(depService.tier)}`,
            borderRadius: '6px',
            padding: '10px',
            width: 150,
            fontSize: '11px',
          },
        });

        // Edge from focal service to dependent
        edges.push({
          id: `${service.entityId}-${depService.entityId}`,
          source: service.entityId,
          target: depService.entityId,
          label: dep.metadata.communicationPattern,
          type: 'smoothstep',
          animated: dep.metadata.dependencyType === 'hard',
          style: {
            stroke: getEdgeColor(dep.metadata.dependencyType),
            strokeWidth: dep.metadata.dependencyType === 'hard' ? 2 : 1,
            strokeDasharray: dep.metadata.dependencyType === 'soft' ? '5,5' : undefined,
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: getEdgeColor(dep.metadata.dependencyType),
          },
          labelStyle: {
            fontSize: '10px',
            fill: 'var(--forge-theme-text-medium, #666)',
          },
          labelBgStyle: {
            fill: 'var(--forge-theme-surface, #fff)',
          },
        });
      }
    });

    return { nodes, edges };
  }, [service, dependencies, dependents]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Update nodes and edges when dependencies change
  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  const handleNodesChange: OnNodesChange = useCallback(
    (changes) => {
      /* c8 ignore next — React Flow only fires node-change events (drag/select)
         after ResizeObserver measures node dimensions; jsdom's ResizeObserver
         stub never does, so this body is unreachable in unit tests. */
      onNodesChange(changes);
    },
    [onNodesChange]
  );

  const handleEdgesChange: OnEdgesChange = useCallback(
    (changes) => {
      /* c8 ignore next — same ResizeObserver limitation as handleNodesChange. */
      onEdgesChange(changes);
    },
    [onEdgesChange]
  );

  // Loading state
  if (loading) {
    return (
      <div className="dependency-graph-placeholder">
        <LoadingSpinner message="Loading dependency graph..." />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="dependency-graph-placeholder">
        <EmptyState title="Failed to load dependencies" description={error} icon="error" />
      </div>
    );
  }

  // Empty state
  if (dependencies.length === 0 && dependents.length === 0) {
    return (
      <div className="dependency-graph-placeholder">
        <EmptyState
          title="No dependencies"
          description={`${service.name} has no dependencies or dependents yet.`}
          icon="info"
        />
      </div>
    );
  }

  return (
    <div className="dependency-graph-canvas">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={handleNodesChange}
        onEdgesChange={handleEdgesChange}
        fitView
        attributionPosition="bottom-left"
      >
        <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
        <Controls />
        <MiniMap
          /* c8 ignore start — MiniMap only renders (and calls nodeColor for)
             nodes whose width/height React Flow has measured via
             ResizeObserver; jsdom's stub never fires, so this callback body
             never runs in unit tests. Every node style.background is always
             set by getServiceTypeColor above, so the fallback is a safety
             net only. */
          nodeColor={(node) => {
            const style = node.style as { background?: string };
            return style?.background || 'var(--forge-theme-surface-container, #eee)';
          }}
          /* c8 ignore stop */
          nodeStrokeWidth={3}
          zoomable
          pannable
        />
        <Panel position="top-right" className="dependency-graph-legend">
          <div className="dependency-graph-legend-title">Legend</div>
          <div className="dependency-graph-legend-row">
            <span
              className="dependency-graph-legend-swatch-line"
              style={{ background: 'var(--forge-theme-primary, #2196F3)' }}
            ></span>
            Hard dependency (animated)
          </div>
          <div className="dependency-graph-legend-row">
            <span
              className="dependency-graph-legend-swatch-line dependency-graph-legend-swatch-line--dashed"
              style={{ background: 'var(--forge-theme-outline-high, #9E9E9E)' }}
            ></span>
            Soft dependency (dashed)
          </div>
          <div className="dependency-graph-legend-muted">
            Left: Dependencies | Right: Dependents
          </div>
          <div className="dependency-graph-legend-section">Service Types:</div>
          <div className="dependency-graph-legend-row">
            <span
              className="dependency-graph-legend-swatch-box"
              style={{ background: 'var(--forge-theme-primary, #2196F3)' }}
            ></span>
            Application
          </div>
          <div className="dependency-graph-legend-row">
            <span
              className="dependency-graph-legend-swatch-box"
              style={{ background: 'var(--forge-theme-success, #4CAF50)' }}
            ></span>
            Integration
          </div>
          <div className="dependency-graph-legend-row">
            <span
              className="dependency-graph-legend-swatch-box"
              style={{ background: 'var(--forge-theme-warning, #FF9800)' }}
            ></span>
            Platform
          </div>
          <div className="dependency-graph-legend-row">
            <span
              className="dependency-graph-legend-swatch-box"
              style={{ background: 'var(--forge-theme-tertiary, #9C27B0)' }}
            ></span>
            Infrastructure
          </div>
          <div className="dependency-graph-legend-row">
            <span
              className="dependency-graph-legend-swatch-box"
              style={{ background: 'var(--forge-theme-secondary, #009688)' }}
            ></span>
            Website
          </div>
          <div className="dependency-graph-legend-section">Tiers (Border):</div>
          <div className="dependency-graph-legend-row">
            <span
              className="dependency-graph-legend-swatch-line"
              style={{ background: 'var(--forge-theme-error, #D32F2F)' }}
            ></span>
            Tier 0 (Critical)
          </div>
          <div className="dependency-graph-legend-row">
            <span
              className="dependency-graph-legend-swatch-line"
              style={{ background: 'var(--forge-theme-warning, #F57C00)' }}
            ></span>
            Tier 1 (High)
          </div>
          <div className="dependency-graph-legend-row">
            <span
              className="dependency-graph-legend-swatch-line"
              style={{ background: 'var(--forge-theme-info, #FBC02D)' }}
            ></span>
            Tier 2 (Medium)
          </div>
          <div className="dependency-graph-legend-row">
            <span
              className="dependency-graph-legend-swatch-line"
              style={{ background: 'var(--forge-theme-success, #689F38)' }}
            ></span>
            Tier 3 (Low)
          </div>
        </Panel>
      </ReactFlow>
    </div>
  );
}
